import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  AppBar, Box, CssBaseline, Divider, Drawer, IconButton,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Typography, useMediaQuery, Avatar, Menu, MenuItem,
  Select, FormControl, Badge, Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Language as LanguageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Scanner as ScannerIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  Timeline as TimelineIcon,
  AllInbox as AllInboxIcon,
  Science as ScienceIcon,
  Category as CategoryIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as SupplierIcon,
  Assignment as OrderListIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { branchAPI } from '../services/api';

// ─── Menu item definition ─────────────────────────────────────────────────────
//
// Each item can have:
//   module       → must have privilege with 'view' operation to see it
//   adminOnly    → only visible to superAdmin
//   alwaysShow   → visible to every logged-in user regardless of privileges
//
// Items with NONE of those flags are visible only to superAdmin (legacy behaviour
// for pages like /register that have no privilege module yet).
// ─────────────────────────────────────────────────────────────────────────────

const ALL_MENU_ITEMS = [
  {
    text: 'navigation.dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    module: 'dashboard',
  },
  {
    text: 'navigation.appointments',
    icon: <CalendarIcon />,
    path: '/appointments',
    module: 'appointments',
  },
  {
    text: 'navigation.doctors',
    icon: <PeopleIcon />,
    path: '/doctors',
    module: 'doctors',
  },
  {
    text: 'navigation.radiologists',
    icon: <PeopleIcon />,
    path: '/radiologists',
    module: 'users',            // radiologists are under the users module
  },
  {
    text: 'navigation.radiologyTechnicians',
    icon: <ScienceIcon />,
    path: '/radiology-technicians',
    module: 'users',
  },
  {
    text: 'navigation.patients',
    icon: <PeopleIcon />,
    path: '/patients',
    module: 'patients',
  },
  {
    text: 'navigation.scans',
    icon: <ScannerIcon />,
    path: '/scans',
    module: 'scans',
  },
  {
    text: 'navigation.scanCategories',
    icon: <CategoryIcon />,
    path: '/scan-categories',
    module: 'scanCategories',
  },
  {
    text: 'navigation.stock',
    icon: <InventoryIcon />,
    path: '/stock',
    module: 'stock',
  },
  {
    text: 'navigation.stockCategories',
    icon: <LabelIcon />,
    path: '/stock-categories',
    module: 'stockCategories',
  },
  {
    text: 'navigation.centralStock',
    icon: <AllInboxIcon />,
    path: '/central-stock',
    module: 'centralStock',
  },
  {
    text: 'navigation.suppliers',
    icon: <SupplierIcon />,
    path: '/suppliers',
    module: 'suppliers',
  },
  {
    text: 'navigation.orderList',
    icon: <OrderListIcon />,
    path: '/order-list',
    module: 'orderList',
  },
  {
    text: 'navigation.expenses',
    icon: <InventoryIcon />,
    path: '/expenses',
    module: 'expenses',
  },
  // ── Admin-only routes ─────────────────────────────────────────────────────
  {
    text: 'navigation.branches',
    icon: <BusinessIcon />,
    path: '/admin/branches',
    module: 'branches',
  },
  {
    text: 'navigation.privilegeManagement',
    icon: <AdminIcon />,
    path: '/admin/privileges',
    adminOnly: true,               // only superAdmin can manage privileges
  },
  {
    text: 'navigation.auditLog',
    icon: <TimelineIcon />,
    path: '/admin/audit',
    adminOnly: true,
  },
  {
    text: 'navigation.representativeManagement',
    icon: <PeopleIcon />,
    path: '/admin/representatives',
    module: 'representatives',
  },
  // ── Always-visible routes ─────────────────────────────────────────────────
  {
    text: 'navigation.profile',
    icon: <AccountCircleIcon />,
    path: '/profile',
    alwaysShow: true,
  },
  {
    text: 'navigation.register',
    icon: <PeopleIcon />,
    path: '/register',
    adminOnly: true,               // only superAdmin can register new users
  },
  {
    text: 'navigation.settings',
    icon: <SettingsIcon />,
    path: '/settings',
    alwaysShow: true,
  },
];

// ─── Permission helper ────────────────────────────────────────────────────────

const canViewModule = (user, isSuperAdmin, module) => {
  if (isSuperAdmin) return true;
  return user?.privileges?.some(
    p => p.module === module && p.operations.includes('view')
  );
};

const isItemVisible = (item, user, isSuperAdmin) => {
  // Always show (profile, settings)
  if (item.alwaysShow) return true;

  // SuperAdmin-only items (privilege management, audit, register)
  if (item.adminOnly) return isSuperAdmin;

  // Module-gated items
  if (item.module) return canViewModule(user, isSuperAdmin, item.module);

  // Fallback — hide if no rule matched and user is not superAdmin
  return isSuperAdmin;
};

// ─────────────────────────────────────────────────────────────────────────────

export const MainLayout = ({ children }) => {
  const { t } = useTranslation();
  const { lang, setLang, isRtl } = useLanguage();
  const { mode, toggleTheme } = useAppTheme();
  const muiTheme = useMuiTheme();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const { unreadCount, notifications, markAsRead, markAllAsRead } = useNotifications();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, isSuperAdmin } = useAuth();

  const [branchName, setBranchName] = useState('');
  const [branchLoading, setBranchLoading] = useState(false);

  // ── Branch name resolution ────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.branch) { setBranchName(''); return; }

    if (typeof user.branch === 'object' && user.branch !== null) {
      if (user.branch.name) {
        setBranchName(user.branch.name);
      } else if (user.branch._id) {
        setBranchLoading(true);
        branchAPI.getById(user.branch._id)
          .then(res => setBranchName(res.data?.data?.name || ''))
          .catch(() => setBranchName(''))
          .finally(() => setBranchLoading(false));
      }
    } else if (typeof user.branch === 'string') {
      setBranchLoading(true);
      branchAPI.getById(user.branch)
        .then(res => setBranchName(res.data?.data?.name || ''))
        .catch(() => setBranchName(''))
        .finally(() => setBranchLoading(false));
    }
  }, [user?.branch]);

  // ── Filter menu items by permission ──────────────────────────────────────
  const visibleMenuItems = ALL_MENU_ITEMS.filter(
    item => isItemVisible(item, user, isSuperAdmin)
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleDrawerToggle = () => setMobileOpen(prev => !prev);
  const handleSidebarToggle = () => setSidebarOpen(prev => !prev);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { handleMenuClose(); logout(); };
  const handleLanguageChange = (e) => setLang(e.target.value);

  const drawerWidth = sidebarOpen ? 320 : 90;

  // ── Sidebar content (shared between mobile/desktop) ───────────────────────
  const SidebarList = () => (
    <List>
      {visibleMenuItems.map(item => (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              if (isMobile) setMobileOpen(false);
            }}
            sx={!sidebarOpen ? { justifyContent: 'center' } : {}}
          >
            <ListItemIcon sx={{ minWidth: sidebarOpen ? (isRtl ? '48px' : '40px') : 0 }}>
              {item.icon}
            </ListItemIcon>
            {sidebarOpen && <ListItemText primary={t(item.text)} />}
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: isRtl ? 'row-reverse' : 'row',
      direction: isRtl ? 'rtl' : 'ltr',
    }}>
      <CssBaseline />

      {/* ── AppBar ── */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          direction: isRtl ? 'rtl' : 'ltr',
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          transition: muiTheme.transitions.create(['width', 'margin'], {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          {/* Mobile drawer toggle */}
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{
              mr: isRtl ? 0 : 2,
              ml: isRtl ? 2 : 0,
              display: { sm: 'none' },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Notifications */}
          <IconButton color="inherit" onClick={e => setNotificationAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={notificationAnchorEl}
            open={Boolean(notificationAnchorEl)}
            onClose={() => setNotificationAnchorEl(null)}
            PaperProps={{ sx: { width: 320, maxHeight: 400 } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">{t('notifications')}</Typography>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={markAllAsRead}>
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Divider />
            <List sx={{ p: 0 }}>
              {notifications.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={t('noNotifications')}
                    sx={{ textAlign: 'center', color: 'text.secondary' }}
                  />
                </ListItem>
              ) : notifications.map(n => (
                <ListItem
                  key={n._id}
                  button
                  alignItems="flex-start"
                  onClick={() => {
                    markAsRead(n._id);
                    if (n.data?.appointmentId) navigate('/appointments');
                  }}
                  sx={{
                    bgcolor: n.isRead ? 'transparent' : 'action.hover',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" fontWeight={n.isRead ? 'normal' : 'bold'}>
                        {n.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.primary" component="span">
                          {n.message}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(n.createdAt).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Menu>

          {/* Theme toggle */}
          <IconButton color="inherit" onClick={toggleTheme} sx={{ ml: 1 }}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* Language selector */}
          <FormControl size="small" sx={{ minWidth: 120, mx: 2 }}>
            <Select
              value={lang}
              onChange={handleLanguageChange}
              sx={{ color: 'inherit' }}
              IconComponent={LanguageIcon}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="ar">العربية</MenuItem>
            </Select>
          </FormControl>

          {/* Branch display */}
          <Box sx={{ mx: 2, display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight={500}>
              {branchLoading ? t('common.loading') : branchName}
            </Typography>
          </Box>

          {/* User avatar menu */}
          <IconButton size="large" edge="end" onClick={handleMenuOpen} color="inherit">
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
              <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
              {t('auth.profile')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>{t('auth.logout')}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Drawer ── */}
      <Box>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : sidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              position: 'fixed !important',
              height: '100%',
              right: isRtl ? '0px' : 'none',
              left: isRtl ? 'none' : '0px',
              border: 'none',
              borderRight: isRtl ? 'none' : '1px solid rgba(0,0,0,0.12)',
              borderLeft: isRtl ? '1px solid rgba(0,0,0,0.12)' : 'none',
              transition: muiTheme.transitions.create('width', {
                easing: muiTheme.transitions.easing.sharp,
                duration: muiTheme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {/* Collapse toggle */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            p: 1,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
          }}>
            <IconButton onClick={handleSidebarToggle}>
              {isRtl
                ? (sidebarOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />)
                : (sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />)
              }
            </IconButton>
          </Box>

          <Box sx={{ overflow: 'auto' }}>
            {sidebarOpen && (
              <Toolbar>
                <Typography variant="h6" noWrap component="div">
                  {t('common.appName')}
                </Typography>
              </Toolbar>
            )}
            {sidebarOpen && <Divider />}
            <SidebarList />
          </Box>
        </Drawer>
      </Box>

      {/* ── Main content ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          mt: '64px',
          direction: isRtl ? 'rtl' : 'ltr',
          textAlign: isRtl ? 'right' : 'left',
          minHeight: 'calc(100vh - 64px)',
          marginLeft: isRtl ? 0 : { sm: `${drawerWidth}px !important` },
          marginRight: isRtl ? { sm: `${drawerWidth}px !important` } : 0,
          transition: muiTheme.transitions.create(['width', 'margin'], {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;